import { useCallback, useEffect, useMemo, useState } from 'react';
import { VoiceCallService } from '../../services/voice-call.service';
import { VOICE_CALL } from '../../constants/voice-call-constants';
import {
  converTimeIntoMintuesAndSeconds,
  isCompleteUsageResponse,
  isWeeklyCap,
  resolveCapWindowFields,
} from './callUsageBannerUtils';

const useCallUsageBannerHook = (learnerId) => {
  const [maxCallDurationPerCall, setMaxCallDurationPerCall] = useState(null);
  const [maxCallDurationPerDay, setMaxCallDurationPerDay] = useState(null);
  const [consumedCallDurationPerDay, setConsumedCallDurationPerDay] = useState(null);
  const [remainingCallDurationPerDay, setRemainingCallDurationPerDay] = useState(null);
  const [voiceMinuteCapPeriod, setVoiceMinuteCapPeriod] = useState(VOICE_CALL.MINUTE_CAP_PERIOD.DAILY);
  const [consumedRawMinutes, setConsumedRawMinutes] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasValidData, setHasValidData] = useState(false);
  const voiceCallService = useMemo(() => new VoiceCallService(), []);

  const determineCallTimeLimitDisplay = useCallback((consumedRaw, remainingRaw) => {
    const formattedConsumed = converTimeIntoMintuesAndSeconds(consumedRaw);
    const formattedRemaining = converTimeIntoMintuesAndSeconds(remainingRaw);
    setConsumedCallDurationPerDay(formattedConsumed);
    setRemainingCallDurationPerDay(formattedRemaining);
    setConsumedRawMinutes(consumedRaw);
  }, []);

  const fetchCallUsage = useCallback(async () => {
    if (!learnerId) {
      return;
    }
    try {
      setIsLoading(true);
      setHasValidData(false);
      const response = await voiceCallService.getCallHistoryUsageForLearner(learnerId);
      if (!response) {
        console.error('Error fetching call usage', response);
        setHasValidData(false);
        return;
      }
      const { maxCallDurationPerCall, voiceMinuteCapPeriod: period } = response;
      const { maxCallDurationPerDayOrPeriod, consumedRaw, remainingRaw } = resolveCapWindowFields(
        period,
        response
      );

      if (isCompleteUsageResponse(maxCallDurationPerCall, maxCallDurationPerDayOrPeriod, consumedRaw, remainingRaw)) {
        setMaxCallDurationPerCall(maxCallDurationPerCall);
        setMaxCallDurationPerDay(maxCallDurationPerDayOrPeriod);
        setVoiceMinuteCapPeriod(
          isWeeklyCap(period) ? VOICE_CALL.MINUTE_CAP_PERIOD.WEEKLY : VOICE_CALL.MINUTE_CAP_PERIOD.DAILY
        );
        determineCallTimeLimitDisplay(consumedRaw, remainingRaw);
        setHasValidData(true);
      } else {
        console.error('Invalid or missing data in call usage response', response);
        setHasValidData(false);
      }
    } catch (error) {
      console.error('Error fetching call usage', error);
      setHasValidData(false);
    } finally {
      setIsLoading(false);
    }
  }, [learnerId, voiceCallService, determineCallTimeLimitDisplay]);

  useEffect(() => {
    if (learnerId) {
      fetchCallUsage();
    }
  }, [learnerId, fetchCallUsage]);

  const updateCallUsages = () => {
    fetchCallUsage();
  };

  return {
    maxCallDurationPerCall,
    maxCallDurationPerDay,
    consumedCallDurationPerDay,
    remainingCallDurationPerDay,
    voiceMinuteCapPeriod,
    consumedRawMinutes,
    isLoading,
    hasValidData,
    updateCallUsages,
  };
};

export default useCallUsageBannerHook;
